import { Icon } from "@/components/icon";
import { CardDescription, CardTitle } from "@/components/ui/card";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { graphql, useLazyLoadQuery } from "react-relay";
import type { afiliadosQuery } from "./__generated__/afiliadosQuery.graphql";
import { toast } from "sonner";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { differenceInMonths, format, isBefore, sub } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/configuracoes/afiliados",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const [_, copyToClipboard] = useCopyToClipboard();
	const { club } = useRouteContext({ from: "/_logged/c/$club" });

	const data = useLazyLoadQuery<afiliadosQuery>(
		graphql`
    query afiliadosQuery($slug: String!) {
      club(slug: $slug) {
        referralCode {
          code
          uses {
            id
            subscription {
              startAt
              club {
                owner {
                  id
                  email
                  avatarFallback
                  photo {
                    signedUrl
                  }
                  displayName
                }
              }
            }
            createdAt
          }
        }
      }
    }
  `,
		{ slug: club.slug },
	);

	const referralUrl = `https://onboarding.scarf.club/cadastro?code=${data.club.referralCode.code}`;

	const validReferrals =
		data.club.referralCode.uses
			?.filter(
				(u) =>
					!isBefore(new Date(u.createdAt), sub(new Date(), { months: 12 })),
			)
			.sort((a, b) =>
				isBefore(new Date(a.createdAt), new Date(b.createdAt)) ? -1 : 1,
			) || [];

	const currentTotalDiscount = Math.min(validReferrals.length * 20, 100);

	return (
		<div className="p-6 flex flex-col gap-6">
			<div className="p-6 border rounded-md flex flex-col gap-6">
				<CardTitle>Seu Link</CardTitle>

				<CardDescription>
					Receba 20% de desconto cumulativo por 12 meses para cada pessoa que
					assinar Scarf com seu link. Quem assinar Scarf com esse link recebe 1
					mês de graça.
				</CardDescription>

				<InputGroup className="max-w-lg">
					<InputGroupInput value={referralUrl} readOnly />

					<InputGroupAddon align="inline-end">
						<button
							type="button"
							onClick={() => {
								copyToClipboard(referralUrl);
								toast.success("Link copiado com sucesso!");
							}}
						>
							<Icon name="Copy" className="size-4" />
						</button>
					</InputGroupAddon>
				</InputGroup>
			</div>

			{validReferrals.length ? (
				<div className="p-6 border rounded-md flex flex-col gap-6">
					<CardTitle>Quem veio com o seu link?</CardTitle>

					{validReferrals.map((us) => (
						<div
							key={us.id}
							className="flex flex-row items-center justify-between gap-6"
						>
							<div className="flex flex-row items-center justify-start gap-3">
								<Avatar>
									<AvatarImage
										src={us.subscription.club.owner.photo?.signedUrl}
									/>

									<AvatarFallback>
										{us.subscription.club.owner.avatarFallback}
									</AvatarFallback>
								</Avatar>

								<div className="flex flex-col">
									<span className="text-sm/5">
										{us.subscription.club.owner.displayName} (
										{format(new Date(us.createdAt), "dd/MM/yyyy")})
									</span>
									<span className="text-xs/5 text-muted-foreground">
										{us.subscription.club.owner.email}
									</span>
								</div>
							</div>

							<div className="flex flex-col items-end">
								<span className="text-sm/5 font-medium">20% OFF</span>
								<span className="text-xs/5 text-muted-foreground">
									Por mais{" "}
									{12 -
										Math.abs(
											differenceInMonths(new Date(), new Date(us.createdAt)),
										)}{" "}
									meses
								</span>
							</div>
						</div>
					))}

					<Separator />

					<div className="flex flex-row items-center justify-between">
						<span className="text-sm text-muted-foreground">
							Convide mais pessoas com o seu link para aumentar seu desconto!
						</span>

						<Badge color="primary">{currentTotalDiscount}% OFF</Badge>
					</div>
				</div>
			) : null}
		</div>
	);
}
